import { useOpacityToggle } from '@amusnet/react-ui'
import { MouseEvent, useEffect, useRef, useState } from 'react'
import { ArrowButton } from './ArrowButton'
import { useConfigurationStore } from '@stores/index'
import { PaginationSection } from './PaginationSection'
import { PlayButton } from './PlayButton'
import { CheckBoxSection } from './CheckBoxSection'
import { ChangeOpacityComponents, GameUIMessageType, IntroScreenType, ViewportType } from 'game-ui-types'
import { IntroScreenPageChangedMessage, IntroScreenStartButtonClickedMessage } from 'game-ui-types'
import { buildGradientStyle } from '@utils/buildGradientStyle'
import { FeatureDescription } from './FeatureDescription'
import { TranslationKey } from '@amusnet/translations'
import { configs } from './configs/configs'

const IntroScreen = () => {
    const introConfig = useConfigurationStore((s) => s.introScreenConfig)
    const viewport = useConfigurationStore((s) => s.viewport)

    const [currentPage, setCurrentPage] = useState(introConfig?.defaultPage || 0)
    const introScreenSectionRef = useRef<HTMLDivElement | null>(null)
    const swipeStartX = useRef(-1)
    const { opacity, isComponentInteractive } = useOpacityToggle(
        ChangeOpacityComponents.INTRO_SCREEN_SECTION,
        introScreenSectionRef
    )

    const paginationDotActiveLayerStyle = buildGradientStyle(
        introConfig?.layout?.paginationDotActiveLayerColors,
        'radial'
    )
    const arrowButtonInnerLayerStyle = buildGradientStyle(introConfig?.layout?.arrowButtonInnerLayerColors, '180deg')
    const playNowButtonBackgroundStyle = buildGradientStyle(introConfig?.layout?.playNowButtonBackgroundColors, '90deg')
    const playNowButtonStyle = buildGradientStyle(introConfig?.layout?.playNowButtonColors, '180deg')
    const layoutConfig = configs[introConfig?.layout?.type || IntroScreenType.INTRO_SCREEN_CONFIG_A][viewport]
    const defaultTextColor = '#FFFFFF'
    const {
        featureTextColor = defaultTextColor,
        featureTextStrokeColor = defaultTextColor,
        featureTextShadowColor = defaultTextColor,
        dontShowAgainTextColor = defaultTextColor,
        dontShowAgainTextStrokeColor = defaultTextColor,
        dontShowAgainTextShadowColor = defaultTextColor,
    } = introConfig?.layout ?? {}
    const featureLocalizationKeysPerPage: Array<TranslationKey> = (introConfig?.featureLocalizationKeysPerPage ??
        []) as Array<TranslationKey>

    const handleOnArrowClick = (_: MouseEvent | undefined, number: number) => {
        setCurrentPage((prev) => {
            const introScreenPageChangedMessage: IntroScreenPageChangedMessage = {
                type: GameUIMessageType.INTRO_SCREEN_PAGE_CHANGED,
                payload: {
                    page: prev + number,
                },
            }

            window.uiBus.postMessage(introScreenPageChangedMessage)

            return prev + number
        })
    }

    const handleOnStartClick = () => {
        const introScreenPageChangedMessage: IntroScreenStartButtonClickedMessage = {
            type: GameUIMessageType.INTRO_SCREEN_START_BUTTON_CLICKED,
        }

        window.uiBus.postMessage(introScreenPageChangedMessage)
    }

    useEffect(() => {
        if (!opacity || !isComponentInteractive || viewport === ViewportType.DESKTOP) {
            return
        }

        const handleTouchStart = (e: PointerEvent) => {
            swipeStartX.current = e.clientX
        }

        const handleTouchEnd = (e: TouchEvent) => {
            const swipeEndX = e.changedTouches[0].clientX
            const swipeDistance = swipeEndX - swipeStartX.current
            const isSwipingRight = swipeStartX.current > swipeEndX
            const canSwipeRight = currentPage !== (introConfig?.pages || 0)
            const canSwipeLeft = !!currentPage
            const swipeThreshold = 60

            if (Math.abs(swipeDistance) < swipeThreshold) {
                return
            }

            if (isSwipingRight && canSwipeRight) {
                handleOnArrowClick(undefined, 1)
            } else if (!isSwipingRight && canSwipeLeft) {
                handleOnArrowClick(undefined, -1)
            }
        }

        window.addEventListener('pointerdown', handleTouchStart)
        window.addEventListener('touchend', handleTouchEnd)

        return () => {
            window.removeEventListener('pointerdown', handleTouchStart)
            window.removeEventListener('touchend', handleTouchEnd)
        }
    }, [currentPage, opacity, isComponentInteractive])

    return (
        <div ref={introScreenSectionRef}>
            <ArrowButton
                onClick={handleOnArrowClick}
                disabled={!currentPage}
                className={layoutConfig.leftArrowButton}
                innerLayerStyle={arrowButtonInnerLayerStyle}
                isDecrement
            />
            <ArrowButton
                onClick={handleOnArrowClick}
                disabled={currentPage === introConfig?.pages}
                className={layoutConfig.rightArrowButton}
                innerLayerStyle={arrowButtonInnerLayerStyle}
            />
            <PlayButton
                onClick={handleOnStartClick}
                className={layoutConfig.playButton}
                backgroundStyle={playNowButtonBackgroundStyle}
                buttonStyle={playNowButtonStyle}
            />
            {featureLocalizationKeysPerPage.length - 1 === introConfig?.pages && (
                <FeatureDescription
                    localizationKey={featureLocalizationKeysPerPage[currentPage]}
                    className={layoutConfig.featureDescription}
                    textColor={featureTextColor}
                    textStrokeColor={featureTextStrokeColor}
                    textShadowColor={featureTextShadowColor}
                />
            )}
            <PaginationSection
                className={layoutConfig.paginationSection}
                currentPage={currentPage}
                pages={introConfig?.pages || 0}
                activeLayerStyle={paginationDotActiveLayerStyle}
            />
            <CheckBoxSection
                className={layoutConfig.checkBoxSection}
                textColor={dontShowAgainTextColor}
                textStrokeColor={dontShowAgainTextStrokeColor}
                textShadowColor={dontShowAgainTextShadowColor}
            />
        </div>
    )
}

export default IntroScreen
